import { isDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import deepEqual from 'fast-deep-equal';
import { memo, useCallback, useState } from 'react';

import { selectIsCurrencyPairInverted } from '@/api/config/selectors.ts';
import { useCashIsChecking } from '@/App/tiles/CashTile/CashContext.tsx';
import { ForwardTile } from '@/App/tiles/CashTile/ForwardTile/index.tsx';
import { SpotTile } from '@/App/tiles/CashTile/SpotTile/index.tsx';
import { ExecutionStatus } from '@/App/tiles/shared/ExecutionStatus.tsx';
import { selectCashTile } from '@/store/cash/selectors/tile.ts';
import { useAppSelector } from '@/store/hooks.ts';

export type CashTileProps = {
  tileId: string;
};

/**
 * Memoized CashTile component because the Grid rerenders a lot.
 */
export const CashTile = memo((props: CashTileProps) => {
  const { tileId } = props;

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const isChecking = useCashIsChecking();

  const [isCommercialMarginVisible, setCommercialMarginVisible] = useState(false);

  const toggleCommercialMarginVisibility = useCallback(
    () =>
      setCommercialMarginVisible((isPreviouslyVisible) => {
        sgwtWebAnalytics?.trackEvent(
          'Cash',
          'Commercial margin visibility toggled',
          isPreviouslyVisible ? 'Hidden' : 'Visible',
        );

        return !isPreviouslyVisible;
      }),
    [sgwtWebAnalytics],
  );

  const { productName, executionStatus, isCurrencyPairInverted } = useAppSelector((state) => {
    const tile = selectCashTile(state, tileId);

    const isCurrencyPairInverted = selectIsCurrencyPairInverted(state, tile?.properties?.currencyPair);

    return {
      productName: tile?.type,
      executionStatus: tile?.execution?.status,
      isCurrencyPairInverted,
    };
  }, deepEqual);

  if (isDefined(executionStatus)) {
    return <ExecutionStatus status={executionStatus} />;
  }

  return (
    <>
      {productName === 'FxSpot' && !isCurrencyPairInverted ? (
        <SpotTile
          tileId={tileId}
          isCommercialMarginVisible={isCommercialMarginVisible}
          onCommercialMarginVisibilityToggle={toggleCommercialMarginVisibility}
        />
      ) : (
        <ForwardTile
          tileId={tileId}
          isCommercialMarginVisible={isCommercialMarginVisible}
          onCommercialMarginVisibilityToggle={toggleCommercialMarginVisibility}
        />
      )}
      {isChecking && (
        <div className="position-absolute z-3 top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-lvl1 bg-opacity-75">
          <div className="spinner blinker-lg" />
        </div>
      )}
    </>
  );
});

export const BACKGROUND_LIGHT_COLOR = 'flat-primary' as 'primary';
